import React, { useState, useEffect } from 'react';
import { InformationCircleIcon, StarIcon } from '@heroicons/react/24/outline';
import { getSuggestedVerses } from '../utils/verseAlgorithm';

const SuggestedVerses = ({ 
  suggestions: suggestionsProp,
  suggestedNextVerses,
  onVerseSelect,
  sentVerses = [],
  disciple = {},
  showHeader = true,
  showTopMatchBadge = false,
}) => {
  const suggestions = Array.isArray(suggestionsProp) ? suggestionsProp : (Array.isArray(suggestedNextVerses) ? suggestedNextVerses : []);
  const [showExplanations, setShowExplanations] = useState(true);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Add effect to handle refreshing animation
  useEffect(() => {
    setIsRefreshing(true);
    const timer = setTimeout(() => setIsRefreshing(false), 500);
    // Debug: log contextInfo for all suggestions
    if (suggestions.length > 0) {
      suggestions.forEach((s, i) => {
        console.log(`Suggestion[${i}] contextInfo:`, s.contextInfo, 'Full suggestion:', s);
      });
    }
    return () => clearTimeout(timer);
  }, [suggestions]); // Trigger when suggestions change

  const renderVerseCard = (suggestion, index, isMain = false) => {
    const hasBeenSent = sentVerses.some(
      sent => sent.verseReference === suggestion.verse.reference
    );

    // Debug logs
    console.log('Full suggestion:', suggestion);
    console.log('Verse data:', suggestion.verse);
    console.log('Question:', suggestion.verse?.question);

    return (
      <div 
        key={`${suggestion.verse.reference}-${index}`} 
        className={`space-y-2 transition-opacity duration-300 ${
          isRefreshing ? 'opacity-0' : 'opacity-100'
        } ${isMain ? 'bg-blue-50 shadow-lg rounded-2xl border-l-8 border-blue-400 p-6 mb-8' : 'bg-white rounded-xl p-4 shadow-sm'} `}
        style={isMain ? { borderLeftWidth: '8px' } : {}}
      >
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-bold ${isMain ? 'text-2xl text-blue-900' : 'text-lg text-gray-900'}`}>{suggestion.verse.reference}</h3>
            {showTopMatchBadge && index === 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-white">
                <StarIcon className="h-3.5 w-3.5" />
                Top Match
              </span>
            )}
          </div>
          <span className={`text-xs px-2 py-0.5 rounded ${isMain ? 'bg-blue-100 text-blue-700 font-semibold' : 'bg-blue-50 text-blue-700'}`}>
            Level {suggestion.verse.level || '1'}
          </span>
        </div>
        <p className={`mt-2 ${isMain ? 'text-lg text-gray-800 font-medium' : 'text-gray-600 text-sm'}`}>{suggestion.verse.text}</p>
        {/* Reflection Question */}
        {suggestion.verse?.question && (
          <div className={`mt-4 ${isMain ? 'bg-blue-100' : 'bg-blue-50'} p-3 rounded-md`}>
            <p className={`text-base ${isMain ? 'text-blue-900 font-semibold' : 'text-blue-800'}`}>
              <span className="font-medium">Reflection Question: </span>
              <span className={isMain ? 'italic' : ''}>{suggestion.verse.question}</span>
            </p>
          </div>
        )}
        {showExplanations && (
          <div className="bg-gray-50 p-2 rounded">
            {suggestion.explanation && (
              <p
                className="text-sm text-gray-500 italic"
                dangerouslySetInnerHTML={{ __html: suggestion.explanation }}
              />
            )}
            {(suggestion.contextInfo || suggestion.verse.contextInfo) && (
              <span className="block text-xs text-gray-400 mt-1">
                {suggestion.contextInfo?.discipleshipStage || suggestion.verse.contextInfo?.discipleshipStage ? `Stage: ${suggestion.contextInfo?.discipleshipStage || suggestion.verse.contextInfo?.discipleshipStage} ` : ''}
                {suggestion.contextInfo?.ageRange || suggestion.verse.contextInfo?.ageRange ? `| Age: ${suggestion.contextInfo?.ageRange || suggestion.verse.contextInfo?.ageRange} ` : ''}
                {suggestion.contextInfo?.relationshipStatus || suggestion.verse.contextInfo?.relationshipStatus ? `| Relationship: ${suggestion.contextInfo?.relationshipStatus || suggestion.verse.contextInfo?.relationshipStatus} ` : ''}
                {suggestion.contextInfo?.christianExperience || suggestion.verse.contextInfo?.christianExperience ? `| Experience: ${suggestion.contextInfo?.christianExperience || suggestion.verse.contextInfo?.christianExperience}` : ''}
              </span>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {suggestion.verse.growthArea && (
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
              {suggestion.verse.growthArea}
            </span>
          )}
          {suggestion.verse.devTopic && (
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
              {suggestion.verse.devTopic}
            </span>
          )}
          {suggestion.verse.category && (
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
              {suggestion.verse.category}
            </span>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={() => onVerseSelect(suggestion.verse)}
            disabled={hasBeenSent}
            className={`px-5 py-2 text-base rounded-lg font-semibold transition-colors duration-200 shadow-sm ${
              hasBeenSent 
                ? 'bg-gray-300 cursor-not-allowed' 
                : isMain 
                  ? 'bg-[#0076BE] text-white hover:bg-[#005991]' 
                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
            }`}
          >
            {hasBeenSent ? 'Already Sent' : 'Create Your Message'}
          </button>
        </div>
      </div>
    );
  };

  // Header rendering (match DiscipleMenu.js)
  const renderHeader = () => (
    <div className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center space-x-5">
          <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {disciple.photoURL ? (
              <img
                src={disciple.photoURL}
                alt={disciple.name || 'Disciple'}
                className="h-full w-full object-cover"
              />
            ) : (
              <svg className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900 truncate">{disciple.name || 'Disciple'}</h1>
            <p className="text-gray-500 text-base truncate">Choose Your Message</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {showHeader && renderHeader()}
      <div className={`p-4 rounded-lg shadow-sm ${showHeader ? 'bg-gray-50' : ''}`}>
        {/* Remove the old title from above the header */}
        <div className="flex justify-between items-center mb-4">
          <div /> {/* Remove the old h2 title */}
          <button
            onClick={() => setShowExplanations(!showExplanations)}
            className="text-xs text-amber-600 hover:text-amber-800"
          >
            {showExplanations ? 'Hide Why Suggested' : 'Show Why Suggested'}
          </button>
        </div>
        {/* Verses List */}
        <div className="space-y-3">
          {/* Desktop View (shown on large screens) */}
          <div className="hidden lg:block space-y-4">
            {suggestions.map((suggestion, index) => 
              renderVerseCard(suggestion, index, index === 0)
            )}
          </div>
          {/* Mobile View (shown on small screens) */}
          <div className="lg:hidden">
            {/* First verse is always visible */}
            {suggestions[0] && renderVerseCard(suggestions[0], 0, true)}
            {/* Show More/Less button if there are additional verses */}
            {suggestions.length > 1 && (
              <div className="relative mt-6 mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center">
                  <button
                    onClick={() => setIsMobileExpanded(!isMobileExpanded)}
                    className="bg-white px-4 py-2 text-sm font-medium text-[#0076BE] hover:text-[#005991]"
                  >
                    {isMobileExpanded ? 'Show Less' : `Show ${suggestions.length - 1} More Suggestions`}
                  </button>
                </div>
              </div>
            )}
            {/* Additional verses with smooth animation */}
            <div className={`space-y-4 transition-all duration-300 ease-in-out overflow-hidden ${
              isMobileExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}>
              {suggestions.slice(1).map((suggestion, index) => 
                renderVerseCard(suggestion, index + 1, false)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestedVerses; 